import { useEffect, useRef, useState } from 'react';
import { OpsMark } from './ui.tsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Camera, Check, CornerDownLeft, CornerDownRight, FileSearch, FileText, GitBranch, Loader2, Mail, Megaphone, MessageCircleQuestion, Paperclip, Pin, RotateCcw, Search, SquarePen, UserPlus, X } from 'lucide-react';
import { ACCEPT, MAX_FILES, prepare, toForm, type Pending } from '../attach.ts';
import { Link } from 'react-router';
import { useTaskDrawer } from '../data.ts';
import { api, type ContactOffer, type Note, type NoteAction } from '../api.ts';

const toolInfo: Record<string, { icon: typeof Search; label: (a: NoteAction) => string }> = {
  get_company_state: { icon: Search, label: () => 'Read the current plan' },
  search_records: { icon: Search, label: (a) => `Searched notes and tasks for “${a.args?.query}”` },
  search_drive: { icon: FileSearch, label: (a) => `Searched Drive for “${a.args?.query}”` },
  search_gmail: { icon: Mail, label: (a) => `Searched Gmail: ${a.args?.query}` },
  list_calendar: { icon: CalendarPlus, label: () => 'Checked the calendar' },
  update_task_timeline: { icon: GitBranch, label: (a) => {
    const moved = a.result?.moved as { title: string; from: string; to: string; cascaded: boolean }[] | undefined;
    return moved?.length ? `Moved ${moved[0].title} (${moved[0].from ?? 'no date'} → ${moved[0].to})${moved.length > 1 ? ` and ${moved.length - 1} dependent task${moved.length > 2 ? 's' : ''}` : ''}` : 'Moved a timeline';
  } },
  update_task: { icon: SquarePen, label: (a) => `Updated a task: ${Object.keys(a.result?.changed ?? {}).join(', ') || 'no change'}` },
  create_task: { icon: SquarePen, label: (a) => `Created “${a.result?.title ?? a.args?.title}”${a.result?.start_date || a.result?.end_date ? ` (${a.result.start_date ?? '…'} → ${a.result.end_date ?? '…'}, ${a.result.date_basis})` : ' (no dates)'}` },
  order_note: { icon: Check, label: (a) => `Order: ${a.result}` },
  order_skipped: { icon: X, label: (a) => `Couldn’t apply an order line: ${a.result?.error ?? ''}` },
  post_daily_briefing: { icon: Megaphone, label: (a) => `Briefed ${(a.result?.audience ?? []).map((e: string) => e.split('@')[0]).join(', ') || 'the team'}: “${a.args?.summary_text}”` },
  revise_briefing: { icon: Megaphone, label: (a) => `Updated the briefing for ${(a.result?.audience ?? []).map((e: string) => e.split('@')[0]).join(', ') || 'the team'} (no new card): “${a.args?.summary_text}”` },
  create_calendar_hold: { icon: CalendarPlus, label: (a) => `Placed a calendar hold: ${a.args?.title}` },
  pin_resource: { icon: Pin, label: (a) => `Pinned ${a.args?.title}` },
  remember_contact: { icon: UserPlus, label: (a) => `${a.result === 'added' ? 'Added' : 'Updated'} ${a.args?.name}${a.args?.organization ? ` (${a.args.organization})` : ''} in People` },
  contact_lookup: { icon: Mail, label: (a) => `Looked up ${a.args?.name} in Gmail & Contacts: ${a.result}` },
  skip_duplicate: { icon: Check, label: (a) => `Skipped a duplicate of “${a.args?.title}” (already tracked)` },
  lookup_drive: { icon: FileSearch, label: (a) => `Looked in Drive for “${a.args?.query}”` },
  lookup_gmail: { icon: Mail, label: (a) => `Looked in Gmail for “${a.args?.query}”` },
  followup_failed: { icon: X, label: () => 'Couldn’t apply that follow-up' },
  lookup_notes: { icon: Search, label: (a) => `Looked through notes for “${a.args?.query}”` },
};

// Brain dump: type what happened; Gemini updates the plan and briefs your partner.
// Other parts of the app can open ⌘K on an existing note (e.g. an applied inbox proposal) to get its Done card.
export const openNoteInOmnibar = (note: Note) => window.dispatchEvent(new CustomEvent('omni:note', { detail: note }));
export const openOmnibarWithFiles = (files: File[], text = '') => window.dispatchEvent(new CustomEvent('omni:files', { detail: { files, text } }));
// kind 'receipt' opens with the camera button highlighted and a receipt hint.
export const openOmnibar = (text = '', kind: 'note' | 'receipt' = 'note') => window.dispatchEvent(new CustomEvent('omni:open', { detail: { text, kind } }));

export function Omnibar({ aiEnabled, model }: { aiEnabled: boolean; model?: string }) {
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState<File[]>([]);
  const [initial, setInitial] = useState<Note | null>(null);
  const [preset, setPreset] = useState<{ text: string; kind: 'note' | 'receipt' }>({ text: '', kind: 'note' });
  useEffect(() => {
    const onNote = (e: Event) => { setInitial((e as CustomEvent<Note>).detail); setOpen(true); };
    const onFiles = (e: Event) => { const d = (e as CustomEvent<{ files: File[]; text: string }>).detail; setSeed(d.files); setPreset({ text: d.text, kind: 'note' }); setOpen(true); };
    const onOpen = (e: Event) => { setPreset((e as CustomEvent<{ text: string; kind: 'note' | 'receipt' }>).detail); setOpen(true); };
    window.addEventListener('omni:note', onNote);
    window.addEventListener('omni:files', onFiles);
    window.addEventListener('omni:open', onOpen);
    return () => { window.removeEventListener('omni:note', onNote); window.removeEventListener('omni:files', onFiles); window.removeEventListener('omni:open', onOpen); };
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(true); }
      if (e.key === 'Escape') setOpen(false);
    };
    // Pasting a screenshot anywhere on the page (outside a text field) opens ⌘K with it attached.
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement;
      if (open || t.closest('input, textarea, [contenteditable]')) return;
      const files = [...(e.clipboardData?.files ?? [])].filter((f) => ACCEPT.includes(f.type));
      if (files.length) { e.preventDefault(); setSeed(files); setOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('paste', onPaste);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('paste', onPaste); };
  }, [open]);
  return (
    <>
      <button className="omni-trigger" onClick={() => setOpen(true)}>
        <OpsMark size={15} aria-hidden />
        <span className="omni-trigger-long">Log an update: what happened?</span>
        <span className="omni-trigger-short">Log an update…</span>
        <kbd>⌘K</kbd>
      </button>
      {open && <OmniDialog onClose={() => { setOpen(false); setSeed([]); setInitial(null); setPreset({ text: '', kind: 'note' }); }} aiEnabled={aiEnabled} model={model} seed={seed} initial={initial} preset={preset} />}
    </>
  );
}

function OmniDialog({ onClose, aiEnabled, model, seed, initial, preset }: { onClose: () => void; aiEnabled: boolean; model?: string; seed: File[]; initial: Note | null; preset: { text: string; kind: 'note' | 'receipt' } }) {
  const qc = useQueryClient();
  const [text, setText] = useState(preset.text);
  const camera = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<Pending[]>([]);
  const [dragging, setDragging] = useState(false);
  const [fileErr, setFileErr] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const addFiles = async (list: File[]) => {
    setFileErr('');
    const room = MAX_FILES - files.length;
    if (list.length > room) setFileErr(`Up to ${MAX_FILES} attachments.`);
    const ready = await Promise.all(list.slice(0, Math.max(0, room)).map(prepare));
    const bad = ready.filter((p) => p.error);
    if (bad.length) setFileErr(bad.map((b) => `${b.file.name}: ${b.error}`).join('; '));
    setFiles((cur) => [...cur, ...ready.filter((p) => !p.error)].slice(0, MAX_FILES));
  };
  useEffect(() => { ref.current?.focus(); if (seed.length) addFiles(seed); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const hasInput = text.trim().length >= 3 || files.length > 0;
  const send = useMutation({
    mutationFn: (act: boolean) => api<{ note: Note }>('/notes', { method: 'POST',
      body: files.length ? toForm({ text, act: String(act) }, files) : { text, act } }),
    onSuccess: () => qc.invalidateQueries(),
  });
  const undo = useMutation({ mutationFn: (id: string) => api<{ conflicts: string[] }>(`/notes/${id}/undo`, { method: 'POST' }), onSuccess: () => qc.invalidateQueries() });
  // The latest version of the note: from the first send, or from a clarification reply.
  const noteId = useRef('');
  const reply = useMutation({
    mutationFn: (b: { answer?: string; save_only?: boolean; add_contacts?: boolean }): Promise<{ note: Note }> => api<{ note: Note }>(`/notes/${noteId.current}/reply`, { method: 'POST', body: b }),
    onSuccess: (d) => { setLatest(d.note); qc.invalidateQueries(); },
  });
  // Whichever request answered last holds the current version of the note.
  const [latest, setLatest] = useState<Note | null>(initial);
  const people = useMutation({
    mutationFn: (add: boolean): Promise<{ note: Note }> => api<{ note: Note }>(`/notes/${noteId.current}/contacts`, { method: 'POST', body: { add } }),
    onSuccess: (d) => { setLatest(d.note); qc.invalidateQueries(); },
  });
  // "Oh wait, also…": another turn on the same note; Gemini amends what it just did.
  const [more, setMore] = useState('');
  const moreRef = useRef<HTMLInputElement>(null);
  const followUp = useMutation({
    mutationFn: (t: string): Promise<{ note: Note }> => api<{ note: Note }>(`/notes/${noteId.current}/followup`, { method: 'POST', body: { text: t } }),
    onSuccess: (d) => { setLatest(d.note); setMore(''); qc.invalidateQueries(); },
  });
  const note = latest ?? reply.data?.note ?? send.data?.note;
  // On a clarifying question: add the people Gemini found to /people when the author answers (on by default).
  const [addPeople, setAddPeople] = useState(true);
  const offers = note?.contact_offers ?? [];
  const withPeople = (b: { answer?: string; save_only?: boolean }) => (offers.length && !b.save_only ? { ...b, add_contacts: addPeople } : b);
  noteId.current = note?.id ?? '';
  const [answer, setAnswer] = useState('');
  const answerRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (note?.status === 'clarify') { setAnswer(''); answerRef.current?.focus(); } }, [note?.status, note?.question]);
  useEffect(() => { if (note?.status === 'done' && !note.reverted) moreRef.current?.focus(); }, [note?.status, note?.turns?.length]);
  // ⌘K only logs updates; questions and research live on the Chat page.
  const submit = (act: boolean) => {
    if (!hasInput || send.isPending) return;
    send.mutate(act);
  };

  return (
    <div className="omni-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !send.isPending) onClose(); }}>
      <div className={`omni${dragging ? ' dragging' : ''}`} role="dialog" aria-label="Brain dump"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles([...e.dataTransfer.files]); }}
        onPaste={(e) => { const f = [...e.clipboardData.files]; if (f.length) { e.preventDefault(); addFiles(f); } }}>
        <button className="icon omni-close" aria-label="Close" onClick={onClose} disabled={send.isPending}><X size={18} /></button>
        {note?.status === 'clarify' ? (
          <div className="clarify">
            {offers.length > 0 && (
              <div className="found-people">
                {offers.map((o, i) => <FoundPerson key={i} o={o} />)}
                <label className="sm"><input type="checkbox" checked={addPeople} onChange={(e) => setAddPeople(e.target.checked)} />{' '}
                  Also {offers.every((o) => o.existing_id) ? 'update' : 'add'} {offers.map((o) => o.name).join(' and ')} {offers.every((o) => o.existing_id) ? 'in' : 'to'} People when I answer</label>
              </div>
            )}
            <div className="clarify-q"><MessageCircleQuestion size={16} aria-hidden /><p>{note.question}</p></div>
            <p className="muted sm clarify-note">Nothing has changed yet. Pick an answer or type your own.</p>
            <div className="chips">
              {(note.options ?? []).map((o, i) => (
                <button key={i} className={`chip${o.save_only ? ' quiet' : ''}`} disabled={reply.isPending}
                  onClick={() => reply.mutate(withPeople(o.save_only ? { save_only: true } : { answer: o.answer }))}>{o.label}</button>
              ))}
            </div>
            <form className="clarify-input" onSubmit={(e) => { e.preventDefault(); if (answer.trim()) reply.mutate(withPeople({ answer: answer.trim() })); }}>
              <input ref={answerRef} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Or answer in your own words: “Yeah, EMS at 2pm”" disabled={reply.isPending} aria-label="Your answer" />
              <button className="btn primary" disabled={!answer.trim() || reply.isPending}>{reply.isPending ? <Loader2 size={14} className="spin" aria-hidden /> : <CornerDownLeft size={14} aria-hidden />} Answer</button>
            </form>
            {reply.isPending && <span className="omni-working" style={{ padding: '0 16px 12px' }}><Loader2 size={14} className="spin" aria-hidden /> Updating the plan with your answer…</span>}
            {reply.error && <p className="err sm" style={{ padding: '0 16px' }}>{(reply.error as Error).message}</p>}
          </div>
        ) : !note ? (
          <>
            <div className="omni-title"><OpsMark size={13} aria-hidden /> Log an update</div>
            <div className="omni-input">
              <OpsMark size={18} className="omni-spark" aria-hidden />
              <textarea ref={ref} value={text} onChange={(e) => setText(e.target.value)} rows={4} disabled={send.isPending}
                placeholder={preset.kind === 'receipt' && !files.length ? 'Snap the receipt with the camera button (or attach a PDF). Add a note if you like: who paid, what it was for.' : 'What happened? Calls, emails, decisions, ideas. Ops updates the timeline, finds the documents and briefs your partner.'}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(true); }} />
              <button className="icon attach-btn" type="button" aria-label="Attach screenshot or PDF" title="Attach screenshot or PDF (or paste / drop)"
                disabled={files.length >= MAX_FILES || send.isPending} onClick={() => picker.current?.click()}><Paperclip size={16} /></button>
              <input ref={picker} type="file" accept={ACCEPT.join(',')} multiple hidden onChange={(e) => { addFiles([...(e.target.files ?? [])]); e.target.value = ''; }} />
              {/* Phones: straight to the camera (receipts, whiteboards, a vendor's screen). */}
              <button className={`icon attach-btn camera-btn${preset.kind === 'receipt' ? ' pulse' : ''}`} type="button" aria-label="Take a photo" title="Take a photo"
                disabled={files.length >= MAX_FILES || send.isPending} onClick={() => camera.current?.click()}><Camera size={16} /></button>
              <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { addFiles([...(e.target.files ?? [])]); e.target.value = ''; }} />
            </div>
            {files.length > 0 && (
              <div className="attach-row">
                {files.map((f) => (
                  <span key={f.id} className="attach-pill" title={`${f.file.name} · ${(f.file.size / 1024 / 1024).toFixed(1)} MB`}>
                    {f.thumb ? <img src={f.thumb} alt="" /> : <FileText size={16} aria-hidden />}
                    <span className="attach-name">{f.file.name}</span>
                    <button type="button" className="icon" aria-label={`Remove ${f.file.name}`} disabled={send.isPending} onClick={() => setFiles((cur) => cur.filter((x) => x.id !== f.id))}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
            {fileErr && <p className="err sm" style={{ padding: '0 16px' }}>{fileErr}</p>}
            {dragging && <div className="drop-hint">Drop screenshots or PDFs to attach</div>}
            <div className="omni-foot">
              {send.isPending ? (
                <span className="omni-working"><Loader2 size={15} className="spin" aria-hidden /> {files.length ? 'Ops is reading the attachment, checking the plan and making changes…' : 'Ops is reading your note, checking the plan and making changes…'}</span>
              ) : (
                <>
                  <span className="muted sm">{aiEnabled ? <>{model && <span className="model-badge" title="Ops runs on this Gemini model (falls back automatically if busy)">powered by {model}</span>} Ops acts on it. Everything it changes can be undone. <kbd>⌘</kbd><kbd>↵</kbd></> : 'AI is off; the note is saved as is.'}</span>
                  <span className="spacer" />
                  <button className="btn" onClick={() => submit(false)} disabled={!hasInput}>Just save</button>
                  {aiEnabled && <button className="btn primary" onClick={() => submit(true)} disabled={!hasInput}><CornerDownLeft size={14} aria-hidden /> Update the plan</button>}
                </>
              )}
            </div>
            {send.error && <p className="err" style={{ padding: '0 16px' }}>{(send.error as Error).message}</p>}
          </>
        ) : (
          <div className="omni-result">
            <div className="omni-result-head">
              {note.status === 'done' ? <Check size={18} className="ok" aria-hidden /> : note.status === 'error' ? <X size={18} className="bad" aria-hidden /> : <Check size={18} aria-hidden />}
              <b>{note.status === 'done' ? 'Done' : note.status === 'error' ? 'Saved, but Ops could not finish' : 'Saved'}</b>
              {note.model && <span className="model-badge" title="The Gemini model Ops used">powered by {note.model}</span>}
            </div>
            {note.email && <p className="muted sm omni-source"><Mail size={13} aria-hidden /> From your inbox: <a href={note.email.url} target="_blank" rel="noreferrer">{note.email.subject || '(no subject)'}</a> · {note.email.from}</p>}
            {note.summary && <p className="omni-summary">{note.summary}</p>}
            {note.error && <p className="err">{note.error}</p>}
            {offers.some((o) => o.status === 'pending') && (
              <div className="found-people">
                {offers.filter((o) => o.status === 'pending').map((o, i) => <FoundPerson key={i} o={o} ask />)}
                <div className="chips" style={{ padding: "8px 0 0" }}>
                  <button className="chip" disabled={people.isPending} onClick={() => people.mutate(true)}><UserPlus size={13} aria-hidden /> {offers.filter((o) => o.status === 'pending').every((o) => o.existing_id) ? 'Update in People' : offers.filter((o) => o.status === 'pending').length > 1 ? 'Add them to People' : 'Add to People'}</button>
                  <button className="chip quiet" disabled={people.isPending} onClick={() => people.mutate(false)}>Don’t add</button>
                </div>
                {people.error && <p className="err sm">{(people.error as Error).message}</p>}
              </div>
            )}
            {(note.actions?.length ?? 0) > 0 && (
              <ol className="omni-steps">
                {note.actions!.map((a, i) => {
                  if (a.tool === 'followup') return <li key={i} className="omni-turn"><CornerDownRight size={14} aria-hidden /> <b>You:</b> {a.args?.text}</li>;
                  const info = toolInfo[a.tool];
                  const Icon = info?.icon ?? Search;
                  return <li key={i} className={a.ok ? '' : 'failed'}><Icon size={14} aria-hidden /> {info ? info.label(a) : a.tool}{!a.ok && <span className="err sm"> · {a.result?.error}</span>}</li>;
                })}
              </ol>
            )}
            {note.status === 'done' && !undo.data && !undo.isPending && !note.reverted && (
              <form className="clarify-input followup" onSubmit={(e) => { e.preventDefault(); if (more.trim() && !followUp.isPending) followUp.mutate(more.trim()); }}>
                <input ref={moreRef} value={more} onChange={(e) => setMore(e.target.value)} disabled={followUp.isPending} aria-label="Follow up"
                  placeholder="Follow up, correct something, or add “oh wait, also…”" />
                <button className="btn primary" disabled={!more.trim() || followUp.isPending}>{followUp.isPending ? <Loader2 size={14} className="spin" aria-hidden /> : <CornerDownLeft size={14} aria-hidden />} Send</button>
              </form>
            )}
            {followUp.isPending && <span className="omni-working" style={{ padding: '0 16px 10px' }}><Loader2 size={14} className="spin" aria-hidden /> Amending what was just done…</span>}
            {followUp.error && <p className="err sm" style={{ padding: '0 16px' }}>{(followUp.error as Error).message}</p>}
            <div className="omni-foot">
              {undo.data && <span className="muted sm">{undo.data.conflicts.length ? `Undone, except ${undo.data.conflicts.join(', ')} (edited since).` : 'Undone.'}</span>}
              <span className="spacer" />
              {note.status === 'done' && !undo.data && <button className="btn" onClick={() => undo.mutate(note.id)} disabled={undo.isPending || followUp.isPending}
                title={note.turns?.length ? 'Reverts every message in this conversation' : undefined}><RotateCcw size={14} aria-hidden /> Undo all</button>}
              <button className="btn" onClick={() => { send.reset(); reply.reset(); people.reset(); followUp.reset(); undo.reset(); setLatest(null); setMore(''); setText(''); setFiles([]); setAddPeople(true); }}>Another</button>
              <button className="btn primary" onClick={onClose}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// "Found Misty Smith (misty@… · EMS Solutions) in your email." Only what Google showed; nothing is saved until the author says so.
function FoundPerson({ o, ask }: { o: ContactOffer; ask?: boolean }) {
  const where = o.found_in === 'Gmail' ? 'in your email' : o.found_in === 'Google Contacts' ? 'in your Google Contacts' : o.found_in === 'Drive' ? 'in Drive' : '';
  const facts = [o.email, o.phone, o.role, o.organization].filter(Boolean);
  return (
    <p className="found-person">
      <UserPlus size={14} aria-hidden />
      <span>{where ? 'Found ' : 'New contact: '}<b>{o.name}</b>{facts.length > 0 && <> ({facts.map((f, i) => <span key={i}>{i ? ' · ' : ''}{f === o.email ? <code>{f}</code> : f}</span>)})</>}
        {where && <> {o.source_url ? <a href={o.source_url} target="_blank" rel="noreferrer">{where}</a> : where}</>}.
        {ask && (o.existing_id ? ' Update their details in People?' : ' Add to People?')}</span>
    </p>
  );
}
